import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Trash2, Save, Clock, MapPin } from 'lucide-react';
import apiService from '../services/api';
import { DUREE_PRESETS, normalizeList } from '../utils/creneauConfig';
import Modal from './Modal';
import ConfirmDialog from './ConfirmDialog';

const DEFAULT_RESSOURCE = {
  nom: '',
  type: 'TERRAIN',
  description: '',
  sport: '',
  surface: '',
  interieur: false,
  eclairage: false,
  capacite: 1,
  prixSupplement: '',
  ordre: 0,
  actif: true,
};

const RessourcesManager = ({ commercant, onUpdateCommercant, loading: parentLoading }) => {
  const [ressources, setRessources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(DEFAULT_RESSOURCE);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [config, setConfig] = useState({
    pasGrilleMinutes: commercant?.pasGrilleMinutes ?? 15,
    dureesAutoriseesMinutes: normalizeList(commercant?.dureesAutoriseesMinutes) || [60, 90, 120],
    moduleCreneaux: commercant?.moduleCreneaux ?? commercant?.priseCreneaux ?? false,
    priseCreneaux: commercant?.priseCreneaux ?? false,
  });
  const [savingConfig, setSavingConfig] = useState(false);

  const commercantId = commercant?.id;

  const fetchRessources = useCallback(async () => {
    if (!commercantId) return;
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.getRessourcesByCommercant(commercantId);
      setRessources(res?.ressources || []);
    } catch (err) {
      console.error(err);
      setError('Impossible de charger les ressources.');
    } finally {
      setLoading(false);
    }
  }, [commercantId]);

  useEffect(() => {
    fetchRessources();
  }, [fetchRessources]);

  useEffect(() => {
    setConfig({
      pasGrilleMinutes: commercant?.pasGrilleMinutes ?? 15,
      dureesAutoriseesMinutes: normalizeList(commercant?.dureesAutoriseesMinutes) || [60, 90, 120],
      moduleCreneaux: commercant?.moduleCreneaux ?? commercant?.priseCreneaux ?? false,
      priseCreneaux: commercant?.priseCreneaux ?? false,
    });
  }, [commercant]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...DEFAULT_RESSOURCE, ordre: ressources.length });
    setModalOpen(true);
  };

  const openEdit = (r) => {
    setEditingId(r.id);
    setForm({
      nom: r.nom,
      type: r.type || 'TERRAIN',
      description: r.description || '',
      sport: r.sport || '',
      surface: r.surface || '',
      interieur: Boolean(r.interieur),
      eclairage: Boolean(r.eclairage),
      capacite: r.capacite ?? 1,
      prixSupplement: r.prixSupplement ?? '',
      ordre: r.ordre ?? 0,
      actif: r.actif !== false,
    });
    setModalOpen(true);
  };

  const handleSaveRessource = async () => {
    if (!form.nom?.trim()) {
      setError('Le nom est obligatoire.');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const payload = {
        ...form,
        nom: form.nom.trim(),
        prixSupplement: form.prixSupplement === '' ? null : parseFloat(form.prixSupplement),
        capacite: parseInt(form.capacite, 10) || 1,
      };
      if (editingId) {
        await apiService.updateRessource(editingId, payload);
      } else {
        await apiService.createRessource(commercantId, payload);
      }
      setModalOpen(false);
      await fetchRessources();
    } catch (err) {
      setError(err.message || 'Erreur lors de la sauvegarde.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await apiService.deleteRessource(deleteTarget.id);
      setDeleteTarget(null);
      await fetchRessources();
    } catch (err) {
      setError(err.message || 'Erreur suppression.');
    }
  };

  const toggleDuree = (minutes) => {
    setConfig((prev) => {
      const set = new Set(prev.dureesAutoriseesMinutes);
      if (set.has(minutes)) set.delete(minutes);
      else set.add(minutes);
      const next = [...set].sort((a, b) => a - b);
      return { ...prev, dureesAutoriseesMinutes: next.length ? next : [60] };
    });
  };

  const handleSaveConfig = async () => {
    if (!onUpdateCommercant) return;
    try {
      setSavingConfig(true);
      await onUpdateCommercant({
        moduleCreneaux: true,
        priseCreneaux: true,
        pasGrilleMinutes: config.pasGrilleMinutes,
        dureesAutoriseesMinutes: config.dureesAutoriseesMinutes,
      });
    } finally {
      setSavingConfig(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="h-5 w-5 text-indigo-600" />
          <h3 className="text-lg font-semibold text-gray-900">Configuration des créneaux</h3>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Durées proposées au client et pas de la grille horaire (souples et paramétrables).
        </p>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-2">Pas de grille</label>
            <select
              value={config.pasGrilleMinutes}
              onChange={(e) => setConfig({ ...config, pasGrilleMinutes: parseInt(e.target.value, 10) })}
              className="w-full rounded-lg border border-gray-200 px-3 py-2"
            >
              <option value={15}>15 minutes</option>
              <option value={30}>30 minutes</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-2">Durées autorisées (client)</label>
            <div className="flex flex-wrap gap-2">
              {DUREE_PRESETS.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => toggleDuree(m)}
                  className={`px-3 py-1 rounded-full text-sm border ${
                    config.dureesAutoriseesMinutes.includes(m)
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-gray-700 border-gray-300'
                  }`}
                >
                  {m} min
                </button>
              ))}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={handleSaveConfig}
          disabled={savingConfig || parentLoading}
          className="mt-4 inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          <Save className="h-4 w-4" />
          Enregistrer la configuration
        </button>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-indigo-600" />
            <h3 className="text-lg font-semibold text-gray-900">Ressources (terrains, salles…)</h3>
          </div>
          <button
            type="button"
            onClick={openCreate}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-sm text-white hover:bg-indigo-700"
          >
            <Plus className="h-4 w-4" />
            Ajouter
          </button>
        </div>

        {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
        {loading ? (
          <p className="text-sm text-gray-500">Chargement…</p>
        ) : ressources.length === 0 ? (
          <p className="text-sm text-gray-500">Aucune ressource. Ajoutez un terrain ou une salle.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {ressources.map((r) => (
              <li key={r.id} className="flex items-center justify-between py-3">
                <div>
                  <p className="font-medium text-gray-900">
                    {r.nom}
                    {!r.actif && <span className="ml-2 text-xs text-amber-600">(inactif)</span>}
                  </p>
                  <p className="text-xs text-gray-500">
                    {r.type}
                    {r.sport ? ` · ${r.sport}` : ''}
                    {r.surface ? ` · ${r.surface}` : ''}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => openEdit(r)} className="p-2 text-gray-500 hover:text-indigo-600">
                    <Edit className="h-4 w-4" />
                  </button>
                  <button type="button" onClick={() => setDeleteTarget(r)} className="p-2 text-gray-500 hover:text-red-600">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editingId ? 'Modifier la ressource' : 'Nouvelle ressource'}>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Nom *</label>
            <input
              className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2"
              placeholder="Indiquez le nom du terrain ou de la salle"
              value={form.nom}
              onChange={(e) => setForm({ ...form, nom: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700">Type</label>
              <select
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2"
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
              >
                <option value="TERRAIN">Terrain</option>
                <option value="SALLE">Salle</option>
                <option value="EQUIPEMENT">Équipement</option>
                <option value="AUTRE">Autre</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Sport / activité</label>
              <input
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2"
                placeholder="Indiquez le sport ou l'activité (facultatif)"
                value={form.sport}
                onChange={(e) => setForm({ ...form, sport: e.target.value })}
              />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Surface</label>
            <input
              className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2"
              list="surface-suggestions"
              placeholder="Synthétique, pelouse, dur…"
              value={form.surface}
              onChange={(e) => setForm({ ...form, surface: e.target.value })}
            />
            <datalist id="surface-suggestions">
              <option value="Synthétique" />
              <option value="Pelouse" />
              <option value="Dur" />
              <option value="Moquette" />
            </datalist>
            <select
              className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              value=""
              onChange={(e) => {
                if (e.target.value) setForm({ ...form, surface: e.target.value });
              }}
            >
              <option value="">Choisir une surface courante…</option>
              <option value="Synthétique">Synthétique</option>
              <option value="Pelouse">Pelouse</option>
              <option value="Dur">Dur</option>
              <option value="Moquette">Moquette</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.interieur} onChange={(e) => setForm({ ...form, interieur: e.target.checked })} />
            Intérieur
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.eclairage} onChange={(e) => setForm({ ...form, eclairage: e.target.checked })} />
            Éclairage
          </label>
          <button
            type="button"
            onClick={handleSaveRessource}
            disabled={saving}
            className="w-full rounded-lg bg-indigo-600 py-2 text-white font-medium disabled:opacity-50"
          >
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Supprimer cette ressource ?"
        message={deleteTarget ? `"${deleteTarget.nom}" sera supprimée ou désactivée si des réservations existent.` : ''}
      />
    </div>
  );
};

export default RessourcesManager;
